import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { SKIP_ERROR_NOTIFICATION } from '@core/http/http-error.interceptor';
import { AUTH_TOKEN_TYPE } from '@core/auth/auth-token.context';

/**
 * `https://shiftat.sa/api/shortener`. Held in a token rather than read from `environment`
 * so it can be varied in tests, matching `PRICING_API_URL`. An empty value — the default
 * wherever it isn't provided — means sharing is unavailable.
 */
export const SHORTENER_API_URL = new InjectionToken<string>('SHORTENER_API_URL', {
  providedIn: 'root',
  factory: () => '',
});

/** Public base a short link's custom id is appended to. */
export const SHARE_LINK_BASE = 'https://shiftat.sa/share/';

/** A short link: `foreignId` is the id of what it points at (a job post), `customId` its editable slug. */
export interface ShortLink {
  foreignId: string;
  customId: string;
}

interface ShortenerRow {
  foreign_id?: unknown;
  custom_id?: unknown;
}

interface ShortenerResponse extends ShortenerRow {
  data?: ShortenerRow | null;
}

@Injectable({ providedIn: 'root' })
export class ShortenerApiService {
  private readonly http = inject(HttpClient);
  private readonly url = inject(SHORTENER_API_URL);

  /** Whether a shortener endpoint is configured at all. */
  readonly enabled = !!this.url;

  /** The short link for a job post, or `null` when it has none yet (404). */
  getByForeignId(foreignId: string): Observable<ShortLink | null> {
    return this.find(new HttpParams().set('foreign_id', foreignId));
  }

  /** The short link using a custom id, or `null` when the id is free (404). */
  getByCustomId(customId: string): Observable<ShortLink | null> {
    return this.find(new HttpParams().set('custom_id', customId));
  }

  /** Saves a new short link; `id` is stored as its `foreign_id`. */
  create(id: string, customId: string): Observable<ShortLink> {
    return this.http
      .post<ShortenerResponse>(this.url, { id, custom_id: customId }, { context: this.context() })
      .pipe(map((res) => toShortLink(res) ?? { foreignId: id, customId }));
  }

  /** Changes the custom id of the short link stored for `id`. */
  update(id: string, customId: string): Observable<ShortLink> {
    return this.http
      .put<ShortenerResponse>(this.url, { id, custom_id: customId }, { context: this.context() })
      .pipe(map((res) => toShortLink(res) ?? { foreignId: id, customId }));
  }

  private find(params: HttpParams): Observable<ShortLink | null> {
    return this.http.get<ShortenerResponse>(this.url, { params, context: this.context() }).pipe(
      map((res) => toShortLink(res)),
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === 404 ? of(null) : throwError(() => error),
      ),
    );
  }

  private context(): HttpContext {
    // 'none' keeps the company JWT off this origin — AUTH_TOKEN_TYPE defaults to 'company',
    // so leaving it unset would attach it. Callers show their own, more specific errors.
    return new HttpContext().set(AUTH_TOKEN_TYPE, 'none').set(SKIP_ERROR_NOTIFICATION, true);
  }
}

/** The row may arrive wrapped in `data` (like `/api/pricing`) or bare; anything unusable is `null`. */
function toShortLink(res: ShortenerResponse | null): ShortLink | null {
  const row = res?.data ?? res;
  if (!row || typeof row.custom_id !== 'string' || !row.custom_id) return null;
  return {
    foreignId: row.foreign_id == null ? '' : String(row.foreign_id),
    customId: row.custom_id,
  };
}
